import React, { useState, useEffect } from 'react';
import { Button, Select, Input, Row, Col, Space, Tag, message, Tooltip, Alert } from 'antd';
import { UndoOutlined, DeleteOutlined } from '@ant-design/icons';

const FormulaBuilder = ({ indicators, value, onChange }) => {
    const [customFormula, setCustomFormula] = useState([]);
    const [customFormulaString, setCustomFormulaString] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [numberInput, setNumberInput] = useState('');
    const [validationErrors, setValidationErrors] = useState([]);
    const [isManualEdit, setIsManualEdit] = useState(false);

    useEffect(() => {
        if (value && !isInitialized) {
            parseExistingFormula(value);
            setCustomFormulaString(value);
            setIsInitialized(true);
        }
    }, [value, isInitialized]);

    const validateFormula = (formulaStr) => {
        const errors = [];
        
        if (!formulaStr || formulaStr.trim() === '') {
            return errors;
        }

        const operatorPattern = /[\+\-\*\/]{2,}/g;
        if (operatorPattern.test(formulaStr)) {
            errors.push('Consecutive operators detected (e.g., ++, --, **)');
        }

        const indicatorPattern = /ind\d+ind\d+/g;
        if (indicatorPattern.test(formulaStr)) {
            errors.push('Consecutive indicators without operator (e.g., ind1ind2)');
        }

        const numberIndicatorPattern = /(?<!\d)(\d+(?:\.\d+)?)ind\d+|ind\d+\s+\d+(?:\.\d+)?(?![\+\-\*\/\)])|(?<!ind)\d+(?:\.\d+)?\s*ind\d+/g;
        const matches = formulaStr.match(numberIndicatorPattern);
        if (matches && matches.some(m => !m.startsWith('ind') || /ind\d+\s+\d/.test(m))) {
            errors.push('Number and indicator must be separated by operator');
        }

        let openParens = 0;
        let closeParens = 0;
        for (let char of formulaStr) {
            if (char === '(') openParens++;
            if (char === ')') closeParens++;
            if (closeParens > openParens) {
                errors.push('Unbalanced parentheses: closing parenthesis before opening');
                break;
            }
        }
        if (openParens !== closeParens) {
            errors.push(`Unbalanced parentheses: ${openParens} opening, ${closeParens} closing`);
        }

        if (/[\+\-\*\/]$/.test(formulaStr.trim())) {
            errors.push('Formula cannot end with an operator');
        }

        if (/^[\+\*\/]/.test(formulaStr.trim())) {
            errors.push('Formula cannot start with operator (except -)');
        }

        if (/\(\s*[\+\*\/]/.test(formulaStr)) {
            errors.push('Opening parenthesis cannot be followed by operator (except -)');
        }

        if (/[\+\-\*\/]\s*\)/.test(formulaStr)) {
            errors.push('Closing parenthesis cannot follow an operator');
        }

    const invalidChars = formulaStr.replace(/[ind\d\+\-\*\/\(\)\s\.]/g, '');
        if (invalidChars.length > 0) {
            errors.push(`Invalid characters detected: ${invalidChars}`);
        }

        const indicatorRegex = /ind(\d+)/g;
        const missing = new Set();
        let m;
        while ((m = indicatorRegex.exec(formulaStr)) !== null) {
            const code = m[1];
            const exists = Object.values(indicators || {}).some(ind => ind.ind_code.toString() === code);
            if (!exists) missing.add(code);
        }
        missing.forEach(code => errors.push(`Indicator ind${code} not found`));
    

        return errors;
    };

    const handleManualFormulaChange = (e) => {
        const newFormula = e.target.value;
        setCustomFormulaString(newFormula);
        setIsManualEdit(true);
        
        const errors = validateFormula(newFormula);
        setValidationErrors(errors);
        
        if (onChange) {
            onChange(newFormula);
        }
    };

    const handleManualFormulaBlur = () => {
        const newFormula = customFormulaString;
        if (newFormula && newFormula.trim()) {
            parseExistingFormula(newFormula);
        } else {
            setCustomFormula([]);
        }
    };

    const parseExistingFormula = (formulaStr) => {
        if (!formulaStr) return;
        
        const tokens = [];
        let currentToken = '';
        
        for (let i = 0; i < formulaStr.length; i++) {
            const char = formulaStr[i];
            
            if (['+', '-', '*', '/', '(', ')'].includes(char)) {
                if (currentToken) {
                    tokens.push(currentToken);
                    currentToken = '';
                }
                tokens.push(char);
            } else {
                currentToken += char;
            }
        }
        
        if (currentToken) {
            tokens.push(currentToken);
        }
        
        const parsedFormula = [];
        tokens.forEach(token => {
            if (['+', '-', '*', '/'].includes(token)) {
                parsedFormula.push({ type: 'operator', value: token });
            } else if (['(', ')'].includes(token)) {
                parsedFormula.push({ type: 'parenthesis', value: token });
            } else if (token.startsWith('ind')) {
                const code = token.replace('ind', '');
                const indicator = Object.values(indicators || {}).find(ind => ind.ind_code.toString() === code);
                if (indicator) {
                    parsedFormula.push({ 
                        type: 'indicator', 
                        value: { code: indicator.ind_code, name: indicator.ind_name_eng } 
                    });
                } else {
                    parsedFormula.push({
                        type: 'indicator_missing',
                        value: { code: code, name: null }
                    });
                }
            } else {
                if (!isNaN(token) && token.trim() !== '') {
                    parsedFormula.push({ type: 'number', value: token });
                }
            }
        });
        
        setCustomFormula(parsedFormula);
    };

    const addToCustomFormula = (type, val) => {
        const newFormula = [...customFormula, { type, value: val }];
        setCustomFormula(newFormula);
    };

    const undoLastComponent = () => {
        if (customFormula.length > 0) {
            const newFormula = customFormula.slice(0, -1);
            setCustomFormula(newFormula);
        }
    };

    const clearFormula = () => {
        setCustomFormula([]);
        setCustomFormulaString('');
        if (onChange) {
            onChange('');
        }
    };

    const addOperator = (operator) => {
        if (customFormula.length === 0) {
            message.warning('Please add an indicator first');
            return;
        }
        const lastItem = customFormula[customFormula.length - 1];
        if (lastItem.type === 'operator') {
            message.warning('Cannot add consecutive operators');
            return;
        }
        if (lastItem.type === 'parenthesis' && lastItem.value === '(') {
            message.warning('Cannot add operator after opening parenthesis');
            return;
        }
        addToCustomFormula('operator', operator);
    };

    const addParenthesis = (paren) => {
        if (paren === '(') {
            if (customFormula.length > 0) {
                const lastItem = customFormula[customFormula.length - 1];
                if (lastItem.type === 'indicator' || (lastItem.type === 'parenthesis' && lastItem.value === ')') || lastItem.type === 'number') {
                    message.warning('Please add an operator before opening parenthesis');
                    return;
                }
            }
        } else if (paren === ')') {
            if (customFormula.length === 0) {
                message.warning('Cannot start with closing parenthesis');
                return;
            }
            const lastItem = customFormula[customFormula.length - 1];
            if (lastItem.type === 'operator' || (lastItem.type === 'parenthesis' && lastItem.value === '(')) {
                message.warning('Cannot add closing parenthesis after operator or opening parenthesis');
                return;
            }
            const openCount = customFormula.filter(item => item.type === 'parenthesis' && item.value === '(').length;
            const closeCount = customFormula.filter(item => item.type === 'parenthesis' && item.value === ')').length;
            if (closeCount >= openCount) {
                message.warning('Unbalanced parentheses');
                return;
            }
        }
        addToCustomFormula('parenthesis', paren);
    };

    const addIndicatorToFormula = (indicatorCode, indicatorName) => {
        if (customFormula.length > 0) {
            const lastItem = customFormula[customFormula.length - 1];
            if (lastItem.type === 'indicator' || (lastItem.type === 'parenthesis' && lastItem.value === ')') || lastItem.type === 'number') {
                message.warning('Please add an operator before adding another indicator');
                return;
            }
        }
        addToCustomFormula('indicator', { code: indicatorCode, name: indicatorName });
    };

    const addNumberToFormula = (number) => {
        if (customFormula.length > 0) {
            const lastItem = customFormula[customFormula.length - 1];
            if (lastItem.type === 'indicator' || (lastItem.type === 'parenthesis' && lastItem.value === ')') || lastItem.type === 'number') {
                message.warning('Please add an operator before adding a number');
                return;
            }
        }
        addToCustomFormula('number', number);
    };

    useEffect(() => {
        if (isManualEdit) {
            setIsManualEdit(false);
            return;
        }
        
        let formulaStr = '';
        customFormula.forEach((item) => {
            if (item.type === 'indicator') {
                formulaStr += `ind${item.value.code}`;
            } else if (item.type === 'operator') {
                formulaStr += item.value;
            } else if (item.type === 'parenthesis') {
                formulaStr += item.value;
            } else if (item.type === 'number') {
                formulaStr += item.value;
            }
        });
        setCustomFormulaString(formulaStr);
        
        const errors = validateFormula(formulaStr);
        setValidationErrors(errors);
        
        if (onChange) {
            onChange(formulaStr);
        }
    }, [customFormula]);

    return (
        <div style={{ padding: 8, border: '1px dashed #d9d9d9', borderRadius: 4 }}>
            <Row gutter={12} style={{ marginBottom: 8 }}>
                <Col span={24}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Select Indicator</label>
                    <Select
                        showSearch
                        autoClearSearchValue={false}
                        placeholder="Select an indicator to add to formula"
                        onChange={(val) => {
                            const indicator = Object.values(indicators || {}).find(ind => `${ind.ind_code} - ${ind.ind_name_eng}` === val);
                            if (indicator) {
                                addIndicatorToFormula(indicator.ind_code, indicator.ind_name_eng);
                            }
                        }}
                        style={{ width: '100%' }}
                        value={null}
                    >
                        {Object.values(indicators || {}).map(indicator => (
                            <Select.Option key={indicator.ind_code} value={`${indicator.ind_code} - ${indicator.ind_name_eng}`}>
                                <Tooltip title={`${indicator.ind_code} - ${indicator.ind_name_eng}`} placement="topLeft">
                                    <span>{indicator.ind_code} - {indicator.ind_name_eng}</span>
                                </Tooltip>
                            </Select.Option>
                        ))}
                    </Select>
                </Col>
            </Row>
            <Row gutter={12} style={{ marginBottom: 8 }}>
                <Col span={24}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Constant Number</label>
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            type="number"
                            placeholder="Enter a constant number"
                            value={numberInput}
                            onChange={(e) => setNumberInput(e.target.value)}
                            onPressEnter={() => {
                                if (numberInput && !isNaN(numberInput)) {
                                    addNumberToFormula(numberInput);
                                    setNumberInput('');
                                }
                            }}
                        />
                        <Button
                            onClick={() => {
                                if (numberInput && !isNaN(numberInput)) {
                                    addNumberToFormula(numberInput);
                                    setNumberInput('');
                                }
                            }}
                            disabled={!numberInput || isNaN(numberInput)}
                        >
                            Add
                        </Button>
                    </Space.Compact>
                </Col>
            </Row>
            <Row gutter={12} style={{ marginBottom: 8 }}>
                <Col span={12}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Operators</label>
                    <Space>
                        <Button onClick={() => addOperator('+')} size="small">+</Button>
                        <Button onClick={() => addOperator('-')} size="small">-</Button>
                        <Button onClick={() => addOperator('*')} size="small">×</Button>
                        <Button onClick={() => addOperator('/')} size="small">÷</Button>
                    </Space>
                </Col>
                <Col span={12} style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <Button 
                        onClick={undoLastComponent} 
                        icon={<UndoOutlined />}
                        disabled={customFormula.length === 0}
                        style={{ flex: 1 }}
                    >
                        Undo
                    </Button>
                    <Button 
                        onClick={clearFormula} 
                        danger 
                        icon={<DeleteOutlined />}
                        disabled={customFormula.length === 0}
                        style={{ flex: 1 }}
                    >
                        Clear
                    </Button>
                </Col>
            </Row>
            <Row gutter={12} style={{ marginBottom: 8 }}>
                <Col span={24}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Parentheses</label>
                    <Space>
                        <Button onClick={() => addParenthesis('(')} size="small">(</Button>
                        <Button onClick={() => addParenthesis(')')} size="small">)</Button>
                    </Space>
                </Col>
            </Row>
            <Row gutter={12} style={{ marginBottom: 8 }}>
                <Col span={24}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Formula Preview</label>
                    <div style={{ 
                        minHeight: '40px', 
                        padding: '8px', 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5'
                    }}>
                        <Space wrap>
                            {customFormula.map((item, index) => (
                                <Tag
                                    key={index}
                                    color={
                                        item.type === 'indicator' ? 'blue' :
                                        item.type === 'indicator_missing' ? 'red' :
                                        item.type === 'parenthesis' ? 'green' :
                                        item.type === 'number' ? 'purple' : 'orange'
                                    }
                                >
                                    {item.type === 'indicator' 
                                        ? `ind${item.value.code}` 
                                        : item.type === 'indicator_missing' 
                                            ? `ind${item.value.code} (not found)`
                                            : item.value}
                                </Tag>
                            ))}
                            {customFormula.length === 0 && (
                                <span style={{ color: '#999' }}>No formula built yet</span>
                            )}
                        </Space>
                    </div>
                </Col>
            </Row>
            <Row gutter={12}>
                <Col span={24}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Formula String</label>
                    <Input 
                        value={customFormulaString} 
                        onChange={handleManualFormulaChange}
                        onBlur={handleManualFormulaBlur}
                        className="custom-input" 
                        placeholder="Formula will appear here or type manually (e.g., ind123+ind456*100)" 
                    />
                    <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
                        You can edit this field directly or use the builder above
                    </div>
                </Col>
            </Row>
            
            {validationErrors.length > 0 && (
                <Row gutter={12} style={{ marginTop: 12 }}>
                    <Col span={24}>
                        <Alert
                            message="Formula Validation Errors"
                            description={
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {validationErrors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            }
                            type="error"
                            showIcon
                        />
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default FormulaBuilder;
